import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { getAbTestVariant } from "@/lib/cookie-helper";
import { sendTelemetryEvent } from "@/lib/telemetry";
import {
    LoginModalView,
    languageSelector,
    useActiveLoginModalContext,
    useShowRegistrationOnboardingContext,
    useUserAuthenticationContext,
} from "@/src/contexts";
import LocationModal from "@/src/location/LocationModal";
import {
    type LoginProviderName,
    deleteCookie,
    getCookie,
    getFromUrl,
    lazyLoadComponentFactory,
    removeFromUrl,
    sendToGA,
    sendToGALoginEvent,
} from "@Components/Modules/Helpers";
import { externalAuthenticationCookie, showOnBoarding } from "@Constants/PortalConstants";
import * as HomepageRoutes from "@Routes/HomepageLinks";

import Resources from "./UserContext.Resources";
import ResourcesEn from "./UserContext.Resources.En";

const LoginModal = lazyLoadComponentFactory(
    () => import(/* webpackChunkName: "Login_LoginModal" */ "../modals/LoginModal"),
);

interface IUserContextState {
    showLoginModal?: boolean;
    showLocationModal?: boolean;
    showMenu: boolean;
}

const googleAdwordsLoginLabel = "h1JgCKju1nAQiOD_mAM";
const googleAdwordsRegisterLabel = "xmg0CNmMuXAQiOD_mAM";

const UserContext: FC = () => {
    const resources = languageSelector(Resources, ResourcesEn);
    const { isAuthenticated } = useUserAuthenticationContext();
    const { activeLoginModal, setActiveLoginModal } = useActiveLoginModalContext();
    const { setShowRegistrationOnboarding } = useShowRegistrationOnboardingContext();
    const refMenuDiv = useRef<HTMLDivElement>(null);
    const { pathname } = useLocation();

    const [state, setState] = useState<IUserContextState>({
        showLocationModal: false,
        showMenu: false,
        showLoginModal: true,
    });

    const setLoginModal = (view: LoginModalView | null): void => {
        if (view !== undefined && view !== activeLoginModal) {
            setActiveLoginModal(view);
        }

        setState((currentState) => ({
            ...currentState,
            showLoginModal: true,
            showLocationModal: false,
        }));
    };

    const hideMenu = () => setState((currentState) => ({ ...currentState, showMenu: false }));

    const closeMenuByEvents = (event: Event) => {
        if (refMenuDiv?.current?.contains(event.target as Node)) {
            return;
        }

        if (state.showMenu) {
            hideMenu();
        }
    };

    const renderAddWords = (adWordsLabel: string): void => {
        const script = window.document.createElement("script");
        script.src = "//www.googleadservices.com/pagead/conversion_async.js";
        script.type = "text/javascript";
        script.async = true;
        script.onload = () => {
            window.google_trackConversion({
                google_conversion_id: 857731080,
                google_conversion_language: "en",
                google_conversion_format: "3",
                google_conversion_color: "ffffff",
                google_conversion_label: adWordsLabel,
                google_remarketing_only: false,
            });
        };

        window.document.body.appendChild(script);
    };

    useEffect(() => {
        window.addEventListener("touchstart", closeMenuByEvents);
        window.addEventListener("scroll", closeMenuByEvents);

        switch (window.location.pathname.toLocaleLowerCase()) {
            case HomepageRoutes.HomeChangeLocation.toLocaleLowerCase():
                setState((s) => ({ ...s, showLocationModal: true }));
                break;
            case HomepageRoutes.ResetPasswordHomepage.toLocaleLowerCase():
                setActiveLoginModal(LoginModalView.NewPassword);
                break;
            default:
                break;
        }

        const authenticationCookie = getCookie(externalAuthenticationCookie);

        if (authenticationCookie) {
            deleteCookie(externalAuthenticationCookie);

            const [eventName, provider, isOnetString] = decodeURIComponent(authenticationCookie).split(":");
            const cashbackVariant = getAbTestVariant("ab_Cashback") ?? undefined;

            const setFbTrack = () => {
                if (typeof window.fbq === "function") {
                    window.fbq("track", "CompleteRegistration");
                } else {
                    setTimeout(setFbTrack, 200);
                }
            };

            switch (eventName) {
                case "MissingEmail":
                    setActiveLoginModal(LoginModalView.Registration);
                    setState((currState) => ({
                        ...currState,
                        loginErrorMessage: resources.missingEmail.replace("{0}", provider),
                    }));
                    break;
                case "Login":
                    sendToGALoginEvent("login", provider as LoginProviderName, cashbackVariant);
                    renderAddWords(googleAdwordsLoginLabel);
                    break;
                case "Registration":
                    sendToGALoginEvent("sign_up", provider as LoginProviderName, cashbackVariant);
                    sendToGA("common_registerSuccess", { variant: cashbackVariant });


                    if (!/true/i.test(isOnetString) && pathname.indexOf("polecaj") < 0) {
                        setShowRegistrationOnboarding(true);
                    }

                    renderAddWords(googleAdwordsRegisterLabel);

                    setFbTrack();
                    break;
                default:
                    break;
            }
        }

        const urlOnBoardingName = getFromUrl(showOnBoarding);

        if (urlOnBoardingName !== null) {
            setState((currState) => ({ ...currState, onBoardingName: urlOnBoardingName }));
            removeFromUrl(showOnBoarding);
        }

        return () => {
            window.removeEventListener("touchstart", closeMenuByEvents);
            window.removeEventListener("scroll", closeMenuByEvents);
        };
    }, []);

    useEffect(() => {
        if (activeLoginModal === LoginModalView.Registration && !isAuthenticated) {
            setLoginModal(LoginModalView.Registration);
        }

        if (LoginModalView[activeLoginModal!]) {
            setLoginModal(activeLoginModal);
        }
    }, [activeLoginModal]);

    useEffect(() => {
        if (state.showMenu) {
            sendTelemetryEvent({
                eventName: "common-MyAccount",
                actionType: "Click",
            });
        }
    }, [state.showMenu]);

    const closeLocationModal = () =>
        setState((currentState) => ({ ...currentState, showLocationModal: false }));

    return (
        <>
            {state.showLoginModal && (
                <LoginModal
                    initialView={activeLoginModal}
                    onLoginSuccess={() => renderAddWords(googleAdwordsLoginLabel)}
                    onRegistrationSuccess={() => renderAddWords(googleAdwordsRegisterLabel)}
                    onClose={() => setState((currentState) => ({ ...currentState, showLoginModal: false }))}
                />
            )}
            {state.showLocationModal && <LocationModal onClose={closeLocationModal} />}
        </>
    );
};

export default UserContext;
